var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { cloneElement } from 'react';
import { Filter, sanitizeListRestProps, SelectInput, TopToolbar, CreateButton, } from 'react-admin';
import { useTranslate } from 'ra-core';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import { readApiHubPreference } from '../preferences';
import { AccessField, CardGrid, Datagrid, List, ListDisplayButton, ListDisplayProvider, LIST_DISPLAY_CARDS, SortButton, SortMenuItem, TruncatedTextField, useListDisplay, } from '../ui';
import { useUserContext } from '../userContexts';
import { isAdminUser } from '../userContexts';
import { ApplicationCard } from './ApplicationCard';
import Inbox from '@material-ui/icons/Inbox';
const defaultSort = { field: 'name', order: 'ASC' };
const listDisplayPreferenceName = 'listDisplay/applications';
const useEmptyStyles = makeStyles(theme => ({
    message: {
        textAlign: 'center',
        opacity: theme.palette.type === 'light' ? 0.5 : 0.8,
        margin: '0 1em',
        color: theme.palette.type === 'light'
            ? 'inherit'
            : theme.palette.text.primary,
    },
    icon: {
        width: '9em',
        height: '9em',
    },
    toolbar: {
        textAlign: 'center',
        marginTop: '2em',
    },
}), { name: 'Empty' });
const Empty = (_a) => {
    var { canCRUD, basePath } = _a, props = __rest(_a, ["canCRUD", "basePath"]);
    const classes = useEmptyStyles(props);
    const translate = useTranslate();
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classes.message },
            React.createElement(Inbox, { className: classes.icon }),
            React.createElement(Typography, { variant: "h6", paragraph: true }, translate('resources.applications.fields.noApplications'))),
        canCRUD && (React.createElement("div", { className: classes.toolbar },
            React.createElement(CreateButton, { basePath: basePath, variant: "contained", color: "primary", label: "resources.applications.actions.addApplication" })))));
};
export const ApplicationList = props => {
    const initialListDisplay = readApiHubPreference(listDisplayPreferenceName, LIST_DISPLAY_CARDS);
    const [userContext] = useUserContext();
    const [canCRUD, setCanCRUD] = React.useState(false);
    React.useEffect(() => {
        if (userContext && isAdminUser(userContext)) {
            setCanCRUD(true);
        }
    }, [userContext]);
    return (React.createElement(ListDisplayProvider, { initialListDisplay: initialListDisplay, preferenceName: listDisplayPreferenceName },
        React.createElement(List, Object.assign({ empty: React.createElement(Empty, { canCRUD: canCRUD }), actions: React.createElement(ApplicationListActions, { canCRUD: canCRUD }), filter: { $select: 'Name,Uuid,ApiKey,Status,Description' }, filters: React.createElement(ApplicationFilter, null), sort: defaultSort, bulkActionButtons: false, component: ApplicationListComponent }, props),
            React.createElement(ApplicationListDisplay, { canCRUD: canCRUD }))));
};
const ApplicationListComponent = props => React.createElement("div", Object.assign({}, props));
const ApplicationFilter = props => {
    return (React.createElement(Filter, Object.assign({}, props),
        React.createElement(SelectInput, { source: "status", choices: [
                {
                    id: 'ENABLED',
                    name: 'resources.applications.status.enabled',
                },
                {
                    id: 'DISABLED',
                    name: 'resources.applications.status.disabled',
                },
                {
                    id: 'APPLICATION_PENDING_APPROVAL',
                    name: 'resources.applications.status.application_pending_approval',
                },
            ] })));
};
const ApplicationListDisplay = props => {
    const { canCRUD } = props;
    const [display] = useListDisplay();
    const classes = useApplicationListStyles();
    if (display === LIST_DISPLAY_CARDS) {
        return (React.createElement(CardGrid, Object.assign({}, props),
            React.createElement(ApplicationCard, { canCRUD: canCRUD })));
    }
    return (React.createElement(Card, { className: classes.root },
        React.createElement(Datagrid, Object.assign({ className: classes.datagrid, rowClick: "show" }, props),
            React.createElement(TruncatedTextField, { source: "name", cellClassName: classes.name }),
            React.createElement(TruncatedTextField, { source: "description", cellClassName: classes.description }),
            React.createElement(AccessField, { source: "status", translationKey: "resources.applications.status", cellClassName: classes.status }))));
};
const useApplicationListStyles = makeStyles(theme => ({
    root: {},
    datagrid: {
        background: theme.palette.common.white,
    },
    name: {},
    status: {},
    description: {
        '& span': {
            maxWidth: '30vw',
            [theme.breakpoints.up('lg')]: {
                maxWidth: '25vw',
            },
        },
    },
}), {
    name: 'Layer7ApplicationList',
});
const useApplicationListActionsStyles = makeStyles(theme => ({
    root: {
        alignItems: 'center',
    },
    button: {
        marginLeft: theme.spacing(),
    },
    createButton: {
        backgroundColor: theme.palette.secondary.lightBlue,
        paddingRight: theme.spacing(2),
        marginRight: theme.spacing(2),
        '&:hover': {
            backgroundColor: theme.palette.secondary.lightBlue,
            color: theme.palette.common.white,
        },
    },
}));
const ApplicationListActions = (_a) => {
    var { className, currentSort, displayedFilters, exporter, filters, filterValues, permanentFilter, resource, showFilter, hasCreate, basePath, canCRUD } = _a, props = __rest(_a, ["className", "currentSort", "displayedFilters", "exporter", "filters", "filterValues", "permanentFilter", "resource", "showFilter", "hasCreate", "basePath", "canCRUD"]);
    const classes = useApplicationListActionsStyles();
    const [display] = useListDisplay();
    return (React.createElement(TopToolbar, Object.assign({ className: classnames(classes.root, className) }, sanitizeListRestProps(props)),
        canCRUD && (React.createElement(CreateButton, { basePath: basePath, variant: "contained", color: "primary", className: classes.createButton, label: "resources.applications.actions.addApplication" })),
        filters &&
            cloneElement(filters, {
                resource,
                showFilter,
                displayedFilters,
                filterValues,
                context: 'button',
            }),
        display === LIST_DISPLAY_CARDS ? (React.createElement(ApplicationListSortButton, { resource: resource, currentSort: currentSort })) : null,
        React.createElement(ListDisplayButton, { className: classes.button })));
};
export const ApplicationListSortButton = props => (React.createElement(SortButton, Object.assign({}, props),
    React.createElement(SortMenuItem, { label: "resources.applications.list.sort.name.asc", sort: SortByNameASC }),
    React.createElement(SortMenuItem, { label: "resources.applications.list.sort.name.desc", sort: SortByNameDESC })));
const SortByNameASC = { field: 'name', order: 'ASC' };
const SortByNameDESC = { field: 'name', order: 'DESC' };
//# sourceMappingURL=ApplicationList.js.map